/**
 * Static Asset Preloader
 * Manages intelligent preloading of static assets for improved performance
 */

interface PreloadConfig {
  criticalAssets: string[];
  routeAssetMap: Record<string, string[]>;
  networkThreshold: 'slow-2g' | '2g' | '3g' | '4g';
  maxConcurrentPreloads: number;
  idleTimeout: number;
}

interface PreloadMetrics {
  assetsPreloaded: number;
  preloadTime: number;
  cacheHits: number;
  networkRequests: number;
}

class StaticAssetPreloader {
  private config: PreloadConfig;
  private metrics: PreloadMetrics;
  private preloadController: AbortController | null = null;
  private idleTimeoutId: number | null = null;

  constructor(config: Partial<PreloadConfig> = {}) {
    this.config = {
      criticalAssets: [],
      routeAssetMap: {
        '/knowledge-base': ['KnowledgeBase-', 'CollabEditor-', 'importDoc-', 'exportMarkdown-'],
        '/quizzes': ['QuizzesBase-', 'TaskLists-', 'ViewVariation-'],
        '/settings': ['Settings-', 'Profile-', 'Members-', 'WorkspaceSettings-'],
        '/billing': ['PlansAndBilling-', 'PaddlePaymentGateway-', 'InvoicePaymentGateway-'],
        '/workspace': ['Members-', 'WsMembers-', 'UserFilter-'],
        '/import': ['importDoc-', 'importMarkdown-', 'jszip.min-'],
        '/stats': ['StatCard-', 'AcknowledgmentsOverview-', 'DatasourceTable-']
      },
      networkThreshold: '3g',
      maxConcurrentPreloads: 3,
      idleTimeout: 2000,
      ...config
    };

    this.metrics = {
      assetsPreloaded: 0,
      preloadTime: 0,
      cacheHits: 0,
      networkRequests: 0
    };

    this.setupIdlePreloading();
  }

  /**
   * Preload critical assets immediately
   */
  async preloadCriticalAssets(): Promise<void> {
    const startTime = performance.now();
    
    try {
      console.log('[PRELOAD] Starting critical asset preload');
      
      // Check if service worker is available
      if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
        console.warn('[PRELOAD] Service worker not available, skipping preload');
        return;
      }

      // Send warmup message to service worker
      await this.sendMessageToServiceWorker('WARMUP_CACHE');
      
      this.metrics.preloadTime = performance.now() - startTime;
      console.log('[PRELOAD] Critical assets preloaded in', this.metrics.preloadTime, 'ms');
      
    } catch (error) {
      console.error('[PRELOAD] Failed to preload critical assets:', error);
    }
  }

  /**
   * Preload assets for a specific route
   */
  async preloadRouteAssets(route: string): Promise<void> {
    try {
      if (!this.shouldPreload()) {
        console.log('[PRELOAD] Skipping route preload due to network conditions');
        return;
      }

      console.log('[PRELOAD] Preloading assets for route:', route);
      
      // Send route preload message to service worker
      await this.sendMessageToServiceWorker('PRELOAD_ROUTE_ASSETS', { route });
      
      this.metrics.assetsPreloaded++;
      
    } catch (error) {
      console.error('[PRELOAD] Failed to preload route assets:', error);
    }
  }

  /**
   * Schedule background preloading during idle time
   */
  scheduleIdlePreload(): void {
    this.cancelPreloading();
    
    this.idleTimeoutId = window.setTimeout(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          this.performIdlePreload();
        }, { timeout: 5000 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => this.performIdlePreload(), 100);
      }
    }, this.config.idleTimeout);
  }

  /**
   * Cancel any ongoing preloading
   */
  cancelPreloading(): void {
    if (this.preloadController) {
      this.preloadController.abort();
      this.preloadController = null;
    }
    
    if (this.idleTimeoutId) {
      clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }
  }

  /**
   * Get preloading metrics
   */
  getMetrics(): PreloadMetrics {
    return { ...this.metrics };
  }

  /**
   * Check if preloading should be performed based on network conditions
   */
  private shouldPreload(): boolean {
    // Check Save-Data preference
    if ('connection' in navigator && (navigator as any).connection?.saveData) {
      console.log('[PRELOAD] Skipping due to Save-Data preference');
      return false;
    }

    // Check if device is low-end (limited memory)
    if ('deviceMemory' in navigator && (navigator as any).deviceMemory < 4) {
      console.log('[PRELOAD] Limiting preload on low-memory device');
      // Allow preload but reduce concurrency
      this.config.maxConcurrentPreloads = 1;
    }

    // Check network speed
    const connection = (navigator as any).connection;
    if (connection?.effectiveType) {
      const speedOrder = ['slow-2g', '2g', '3g', '4g'];
      const currentSpeed = speedOrder.indexOf(connection.effectiveType);
      const thresholdSpeed = speedOrder.indexOf(this.config.networkThreshold);
      
      if (currentSpeed < thresholdSpeed) {
        console.log('[PRELOAD] Skipping due to slow connection:', connection.effectiveType);
        return false;
      }

      // Adjust preload strategy based on connection
      if (connection.effectiveType === '3g') {
        this.config.maxConcurrentPreloads = 2; // Reduce concurrency on 3G
      } else if (connection.effectiveType === '4g') {
        this.config.maxConcurrentPreloads = 4; // Increase on 4G
      }
    }

    // Check battery status (if available)
    if ('getBattery' in navigator) {
      (navigator as any).getBattery().then((battery: any) => {
        if (battery.charging === false && battery.level < 0.2) {
          console.log('[PRELOAD] Limiting preload due to low battery');
          this.config.maxConcurrentPreloads = 1;
        }
      }).catch(() => {
        // Battery API not available, continue normally
      });
    }

    // Check if user is on a metered connection
    if (connection?.type === 'cellular') {
      console.log('[PRELOAD] Reducing preload on cellular connection');
      this.config.maxConcurrentPreloads = Math.min(this.config.maxConcurrentPreloads, 2);
    }

    return true;
  }



  /**
   * Get the current route from the URL
   */
  private getCurrentRoute(): string | null {
    const path = window.location.pathname;
    
    // Match against known routes
    for (const route of Object.keys(this.config.routeAssetMap)) {
      if (path.includes(route)) {
        return route;
      }
    }
    
    return null;
  }

  /**
   * Send a message to the service worker
   */
  private async sendMessageToServiceWorker(type: string, data?: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!navigator.serviceWorker.controller) {
        reject(new Error('No service worker controller'));
        return;
      }

      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data);
      };

      navigator.serviceWorker.controller.postMessage(
        { type, data },
        [messageChannel.port2]
      );

      // Timeout after 5 seconds
      setTimeout(() => {
        reject(new Error('Service worker message timeout'));
      }, 5000);
    });
  }

  /**
   * Setup automatic idle preloading with route prediction
   */
  private setupIdlePreloading(): void {
    // Schedule preload on page load
    if (document.readyState === 'complete') {
      this.scheduleIdlePreload();
    } else {
      window.addEventListener('load', () => {
        this.scheduleIdlePreload();
      });
    }

    // Setup route change detection and prediction
    this.setupRoutePreloading();

    // Setup link hover preloading
    this.setupLinkHoverPreloading();

    // Cancel preloading when user becomes active
    ['mousedown', 'touchstart', 'keydown', 'scroll'].forEach(event => {
      document.addEventListener(event, () => {
        this.cancelPreloading();
      }, { passive: true, once: true });
    });
  }

  /**
   * Setup route-based preloading with navigation prediction
   */
  private setupRoutePreloading(): void {
    let lastUrl = window.location.href;
    let navigationHistory: string[] = [];

    const handleRouteChange = () => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrl) {
        navigationHistory.push(lastUrl);
        
        // Keep only last 10 routes for prediction
        if (navigationHistory.length > 10) {
          navigationHistory = navigationHistory.slice(-10);
        }

        lastUrl = currentUrl;
        
        // Preload assets for current route
        const currentRoute = this.getCurrentRoute();
        if (currentRoute) {
          this.preloadRouteAssets(currentRoute);
        }

        // Predict and preload next likely routes
        this.preloadPredictedRoutes(navigationHistory);
      }
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleRouteChange);

    // Listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      setTimeout(handleRouteChange, 0);
    };

    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      setTimeout(handleRouteChange, 0);
    };

    // Initial route preload
    const currentRoute = this.getCurrentRoute();
    if (currentRoute) {
      setTimeout(() => this.preloadRouteAssets(currentRoute), 1000);
    }
  }

  /**
   * Setup link hover preloading
   */
  private setupLinkHoverPreloading(): void {
    let hoverTimeout: number | null = null;

    const handleLinkHover = (event: MouseEvent) => {
      const link = (event.target as Element)?.closest('a[href]') as HTMLAnchorElement;
      if (!link || !link.href) return;

      // Only preload internal links
      if (link.hostname !== window.location.hostname) return;

      // Clear previous timeout
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }

      // Preload after 100ms hover (user intent signal)
      hoverTimeout = window.setTimeout(() => {
        const route = this.getRouteFromUrl(link.href);
        if (route && this.config.routeAssetMap[route]) {
          console.log('[PRELOAD] Hover preloading for route:', route);
          this.preloadRouteAssets(route);
        }
      }, 100);
    };

    const handleLinkLeave = () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }
    };

    // Use event delegation for better performance
    document.addEventListener('mouseover', handleLinkHover, { passive: true });
    document.addEventListener('mouseout', handleLinkLeave, { passive: true });
  }

  /**
   * Preload assets for predicted routes based on navigation history
   */
  private async preloadPredictedRoutes(history: string[]): Promise<void> {
    if (history.length < 2) return;

    // Simple prediction: if user went A -> B -> C, they might go back to B
    const predictions = this.predictNextRoutes(history);
    
    for (const predictedUrl of predictions.slice(0, 2)) { // Limit to 2 predictions
      const route = this.getRouteFromUrl(predictedUrl);
      if (route && this.config.routeAssetMap[route]) {
        console.log('[PRELOAD] Predictive preloading for route:', route);
        await this.preloadRouteAssets(route);
      }
    }
  }

  /**
   * Simple route prediction based on navigation patterns
   */
  private predictNextRoutes(history: string[]): string[] {
    const predictions: string[] = [];
    
    // Pattern 1: User might go back to previous route
    if (history.length >= 1) {
      predictions.push(history[history.length - 1]);
    }

    // Pattern 2: Common navigation patterns
    const currentRoute = this.getCurrentRoute();
    const commonPatterns: Record<string, string[]> = {
      '/knowledge-base': ['/settings', '/quizzes'],
      '/settings': ['/knowledge-base', '/billing'],
      '/quizzes': ['/knowledge-base', '/settings'],
      '/billing': ['/settings']
    };

    if (currentRoute && commonPatterns[currentRoute]) {
      predictions.push(...commonPatterns[currentRoute].map(route => 
        window.location.origin + route
      ));
    }

    return [...new Set(predictions)]; // Remove duplicates
  }

  /**
   * Get route from full URL
   */
  private getRouteFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      
      // Match against known routes
      for (const route of Object.keys(this.config.routeAssetMap)) {
        if (path.includes(route)) {
          return route;
        }
      }
      
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Enhanced idle preloading with intersection observer for visible links
   */
  private async performIdlePreload(): Promise<void> {
    if (!this.shouldPreload()) return;

    try {
      console.log('[PRELOAD] Performing idle preload');
      
      // Preload assets for the current route
      const currentRoute = this.getCurrentRoute();
      if (currentRoute && this.config.routeAssetMap[currentRoute]) {
        await this.preloadRouteAssets(currentRoute);
      }

      // Preload assets for visible links using Intersection Observer
      this.preloadVisibleLinks();
      
    } catch (error) {
      console.error('[PRELOAD] Idle preload failed:', error);
    }
  }

  /**
   * Preload assets for links currently visible in viewport
   */
  private preloadVisibleLinks(): void {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const link = entry.target as HTMLAnchorElement;
          const route = this.getRouteFromUrl(link.href);
          
          if (route && this.config.routeAssetMap[route]) {
            console.log('[PRELOAD] Visible link preloading for route:', route);
            this.preloadRouteAssets(route);
            observer.unobserve(link); // Only preload once
          }
        }
      });
    }, {
      rootMargin: '50px' // Start preloading when link is 50px from viewport
    });

    // Observe all internal links
    const links = document.querySelectorAll('a[href]');
    links.forEach(link => {
      const href = (link as HTMLAnchorElement).href;
      if (href && new URL(href).hostname === window.location.hostname) {
        observer.observe(link);
      }
    });

    // Clean up observer after 30 seconds
    setTimeout(() => observer.disconnect(), 30000);
  }
}

// Export singleton instance
export const staticAssetPreloader = new StaticAssetPreloader();
export default staticAssetPreloader;